import React, { useEffect, useState } from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import TextInput from "../../input/TextInput";
import DateInput from "../../input/DateInput";
import Tooltip from "../../input/Tooltip";
import NumericInput from "../../input/NumericInput";
import SsnInput from "../../input/SsnInput";
import PhoneInput from "../../input/PhoneInput";
import EmployeeAddress from "../../structure/EmployeeAddress";
import { useFormikContext } from "formik";
import { useAuth } from "oidc-react";
import Search from "../../input/Search";
import { EmployerFormValues } from "../../common/EmployerFormValues";
import HorizontalRule from "../../structure/HorizontalRule";
import { Employer, Member } from "../../../generated/graphql";
import _ from "lodash";
import { useMember } from "../../../hooks/useMember";
import { useMembers } from "../../../hooks/useMembers";
import SelectDropdown from "../../input/SelectDropdown";
import TwoColumnDiv from "../../structure/TwoColumnDiv";
import Checkbox from "../../input/Checkbox";
import { useEmployer } from "../../../hooks/useEmployer";
import ShowHideDiv from "../../structure/ShowHideDiv";
import { Gender } from "../../enums/Gender";
import { getValueByKey } from "../../enums/KeyFinder";
import WorkState from "../../structure/WorkState";
import NameInput from "../../input/NameInput";

const AboutEmployee = () => {
    const { values, setFieldValue } = useFormikContext<EmployerFormValues>();
    const [selectedMember, setSelectedMember] = useState<Member>();
    const auth = useAuth();
    const userData = auth.userData;
    const signedIn = !!userData;

    const policyNumber = values.policyNumber;
    const newEmployee = values.newEmployee;

    const { data: employerData } = useEmployer(auth)
    const employer = employerData?.findEmployer as Employer
    const policies = employer?.policies;
    const policy = policies?.find(p => p.id === policyNumber);
    const units = policy?.units
    const memberGroups = policy?.memberGroups;

    useEffect(() => {
        if(policies?.length === 1) {
            setFieldValue('employerName', policies[0].name)
        }
    }, [policies, setFieldValue])

    useEffect(() => {
        if(units?.length === 1) {
            setFieldValue('unitName', units[0].name)
        }
    }, [units, setFieldValue])

    useEffect(() => {
        if(!signedIn) {
            setFieldValue('newEmployee', true)
        } else if(signedIn) {
            setFieldValue('newEmployee', false)
        }
    }, [signedIn, setFieldValue])

    const { data: membersData } = useMembers(auth, {
        onCompleted: (data) => {
            if (data?.findMembers?.length <= 0) {
                setFieldValue('newEmployee', true)
            }
        },
        onError: () => {
            setFieldValue('newEmployee', true)
        }
    })
    const members = membersData?.findMembers
    const { data: memberData } = useMember(selectedMember?.policyId, selectedMember?.id, undefined, auth, {
        onCompleted: (data) => {
            const member = data?.findMember
            if(!member) {
                return;
            }
            setFieldValue('firstName', member.firstName)
            setFieldValue('lastName', member.lastName)
            setFieldValue('dateOfBirth', member.dateOfBirth)
            setFieldValue('gender', getValueByKey(Gender, member.gender))
            setFieldValue('policyNumber', member.policyId)
            setFieldValue('privacyId', member.id)
            setFieldValue('socialSecurityNumber', member.ssn);
            setFieldValue('phoneNumber', member.homePhoneNumber);
            const residentialAddress = member.residentialAddress;
            setFieldValue('streetAddress', residentialAddress?.lineOne);
            setFieldValue('city', residentialAddress?.city);
            setFieldValue('state', residentialAddress?.stateAbbreviation);
            setFieldValue('zipCode', residentialAddress?.zip);
            const employmentInformation = member.employmentInformation;
            setFieldValue('dateOfHire', employmentInformation?.dateOfHire);
            setFieldValue('hoursWorkedPerWeek', employmentInformation?.hoursWorked?.toString());
            setFieldValue('employerName', employmentInformation?.employerName);
            setFieldValue('unitNumber', employmentInformation?.billGroupId);
            setFieldValue('unitName', employmentInformation?.billGroupName);
            setFieldValue('memberGroupName', employmentInformation?.memberGroupName);
            setFieldValue('workStateSameAsResidencyState', '')
            setFieldValue('workState', '')
        }
    })
    const member = memberData?.findMember;

    const sortedMembers = _.cloneDeep(members as any as Member[] ?? []).sort((a: Member, b: Member) => {
        const result = a.firstName.localeCompare(b.firstName);
        return result !== 0 ? result : a.lastName.localeCompare(b.lastName);
    })

    return (
        <QuestionPanel headerText="Let's gather some information about your employee."
                       detailsText="Your employee's privacy is important to us. We'll only use this contact information for official communication regarding your employee's claim."
                       panelContent={
                           <div>
                               {members?.length > 0 && <>
                                   {!newEmployee && <Search id="employee-search" label="Employee search"
                                                            placeholder="Start typing a name or Principal member ID"
                                                            maxResults={10}
                                                            options={sortedMembers?.map((m, index) => {
                                                                return {
                                                                    id: String(index),
                                                                    text: `${m.firstName} ${m.lastName} (Date of birth: ${m.dateOfBirth}, member ID: ${m.id})`,
                                                                    value: `${m.firstName} ${m.lastName} ${m.dateOfBirth} ${m.id}`,
                                                                    onSelect: () => {
                                                                        setSelectedMember(m);
                                                                    }
                                                                }
                                                            })}/>}
                                   <Checkbox id="new-employee" label="Can't find the employee you're looking for?"
                                             changeCallback={(target: HTMLInputElement) => {
                                                 if (target.checked) {
                                                     setFieldValue('firstName', '')
                                                     setFieldValue('lastName', '')
                                                     setFieldValue('dateOfBirth', '')
                                                     setFieldValue('gender', '')
                                                     setFieldValue('policyNumber', '')
                                                     setFieldValue('privacyId', '')
                                                     setFieldValue('socialSecurityNumber', '')
                                                     setFieldValue('phoneNumber', '')
                                                     setFieldValue('streetAddress', '')
                                                     setFieldValue('city', '')
                                                     setFieldValue('state', '')
                                                     setFieldValue('zipCode', '')
                                                     setFieldValue('dateOfHire', '');
                                                     setFieldValue('hoursWorkedPerWeek', '');
                                                     const moreThanOnePolicy = lengthGreaterThanOne(policies);
                                                     if(moreThanOnePolicy) {
                                                         setFieldValue('employerName', '');
                                                     }
                                                     if(moreThanOnePolicy || lengthGreaterThanOne(units)) {
                                                         setFieldValue('unitNumber', '');
                                                         setFieldValue('unitName', '');
                                                     }
                                                     if(moreThanOnePolicy || lengthGreaterThanOne(memberGroups)) {
                                                         setFieldValue('memberGroupName', '');
                                                     }
                                                     setFieldValue('workStateSameAsResidencyState', '')
                                                     setFieldValue('workState', '')
                                                     setSelectedMember(undefined);
                                                 }
                                             }}/>
                                   <HorizontalRule/>
                               </>}
                               <ShowHideDiv show={!member?.ssn}>
                                   <SsnInput id="social-security-number" label="Employee's Social Security number" required={true}/>
                               </ShowHideDiv>
                               <ShowHideDiv show={!signedIn}>
                                   <NumericInput id="privacy-id" label="Employee's Principal member ID" required={false}
                                                 maxLength={9}
                                                 labelContent={<Tooltip text="You can find this 9-digit number on your principal.com employer account or in billing documents."/>}/>
                               </ShowHideDiv>
                               {(!signedIn || newEmployee) && <div>
                                   <NameInput id="first-name" label="Employee's first name" required={signedIn}/>
                                   <TextInput id="last-name" label="Employee's last name" required={signedIn} maxLength={1024}/>
                                   <DateInput id="date-of-birth" label="Employee's date of birth" required={signedIn}/>
                                   <TwoColumnDiv leftContent={buildPolicyNumber()} rightContent={buildUnitNumber()}/>
                                   {buildMemberGroupName()}
                                   <PhoneInput id="phone-number" label="Employee's phone number" required={true}/>
                                   <HorizontalRule/>
                                   <EmployeeAddress/>
                               </div>}
                               <SelectDropdown id="gender" label="Employee's gender" required={true}
                                               options={(values.pfml
                                                       ? Object.keys(Gender)
                                                       : Object.keys(Gender).filter(gender => gender !== 'NON_BINARY')
                                               ).map(p => ({
                                                   id: p.replace(/_/g, '-').toLowerCase(),
                                                   text: (Gender as any)[p]
                                               }))}/>
                               <WorkState/>
                           </div>
                       }
        >
        </QuestionPanel>
    );

    function buildPolicyNumber() {
        if (!signedIn || !policies || !policies?.length) {
            return <NumericInput id="policy-number" label="Account number" required={false}
                                 maxLength={7} labelContent={<Tooltip text="You can find this 7-digit number in your policy booklet."/>}/>
        }
        return <SelectDropdown id="policy-number" label="Employer" required={true}
                               changeCallback={(e: any) => {
                                   setFieldValue('employerName', policies?.find(p => p.id === e.value)?.name)
                                   setFieldValue('unitNumber', '')
                                   setFieldValue('unitName', '')
                                   setFieldValue('memberGroupName', '')
                               }}
                               options={policies.map((p, index) => {
                                   return {
                                       id: String(index),
                                       text: `${p.name} (${p.id})`,
                                       value: p.id
                                   }
                               })}/>
    }

    function buildUnitNumber() {
        if (!signedIn || !policies || !policies?.length) {
            return <NumericInput id="unit-number" label="Bill group number" required={false} maxLength={5}
                                 labelContent={<Tooltip
                                     text="You can find this 5 digit number in your policy booklet, depending on what bill group your employee is in."/>}/>
        }
        return <SelectDropdown id="unit-number" label="Bill group" required={true}
                               changeCallback={(e: any) => {
                                   setFieldValue('unitName', policy?.units?.find(u => u.id === e.value)?.name ?? "")
                               }}
                               options={policy ? policy.units.map((u, index) => {
                                   return {
                                       id: String(index),
                                       text: `${u.name} (${u.id})`,
                                       value: u.id
                                   }
                               }) : []}/>
    }

    function buildMemberGroupName() {
        if (!signedIn || !policies || !policies?.length) {
            return null;
        }
        return <SelectDropdown id="member-group-name" label="Job classification" required={true}
                               options={policy ? policy.memberGroups.map((u, index) => {
                                   return {
                                       id: String(index),
                                       text: u.name,
                                   }
                               }) : []}/>
    }

    function lengthGreaterThanOne(array: object[] | undefined) {
        const length = array?.length;
        return length && length > 1
    }
};
export default AboutEmployee;